import { FormEvent, useEffect, useState, lazy } from "react"
import styles from "../../assets/styles/dashboard.module.css"
import { Tab, Tabs } from "@mui/material"
import { a11yProps, CustomTabPanel } from "../../components/common/CustomTabPanel"
import { addManageEmployeeApi, enableDisableEmployeeApi, getManageEmployeeApi } from "../../utils/api/services"
import { toast } from "react-toastify"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch, RootState } from "../../utils/store"
import moment from "moment"
import { getCiyReducer, getStateReducer } from "../../utils/store/profile/ProfileSlice"
import { useModuleAccess } from "../../components/hooks/useModuleAccess"
import allModules from "../../utils/functions/modules"

const CustomContainer = lazy(() => import("../../components/common/CustomContainer"))
const AllTable = lazy(() => import("../../components/manageEmploye/AllTable"))
const AddedTable = lazy(() => import("../../components/manageEmploye/AddedTable"))
const DisableTable = lazy(() => import("../../components/manageEmploye/DisableTable"))
const ConfirmModal = lazy(() => import("../../components/common/ConfirmModal"))
const AddEmployee = lazy(() => import("../../components/manageEmploye/AddEmployee"))

const ManageEmploye = () => {
    const [employeeData, setEmployeeData] = useState<EmployeeStatus | null>(null);
    const [loading, setLoading] = useState(false)
    const [value, setValue] = useState(0);
    const [searchValue, setSearchValue] = useState('')
    const [openAddModal, setopenAddModal] = useState(false)
    const [openConfirmModal, setOpenConfirmModal] = useState(false)
    const [btnLoading, setBtnLoading] = useState(false)
    const [particularData, setParticularData] = useState<EmployeeDetail | null>(null)
    const [formStates, setFormStates] = useState<addEmploymentFormTypes>({
        name: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        gender: '',
        currentLocation: '',
        city: '',
        state: '',
        zipCode: '',
    });
    const { stateData, cityData } = useSelector((state: RootState) => state.profile);
    const { LoginData } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch<AppDispatch>();

    const manageEmployee = useModuleAccess(allModules.manageEmployee)

    const handleOpenConfirmModal = (item: EmployeeDetail) => {
        setOpenConfirmModal(true)
        setParticularData(item)
    }
    const CloseConfirmModal = () => {
        setOpenConfirmModal(false)
        setParticularData(null)
    }

    const confirmSubmit = async () => {
        try {
            const sendData = {
                "companyId": LoginData?.companyId,
                "patientId": particularData?.patientId,
                "status": particularData?.status === "Active" ? 0 : 1
            }
            setBtnLoading(true)
            const result = await enableDisableEmployeeApi(sendData);
            if (result?.success) {
                setBtnLoading(false)
                if (LoginData) {
                    fetchManageEmployee({ companyId: LoginData.companyId, status: value, search: '' });
                }
                CloseConfirmModal();
                setParticularData(null)
            }
        } catch (error: any) {
            setBtnLoading(false)
            toast.error(error?.data?.errors || error?.message);
        }
    }
    const closeAddModal = () => {
        setopenAddModal(false)
    }
    const handleInputChange: HandleInputChangeType = (e) => {
        const { name, value } = e.target;
        setFormStates({ ...formStates, [name]: value })
    };

    const handleSelectChange: HandleSelectChangeType = (e) => {
        const { name, value } = e.target;
        setFormStates({ ...formStates, [name]: value })
        if (name === "state") {
            const stateCode = stateData?.filter((item) => item?.id === value);
            if (stateCode?.length > 0) {
                dispatch(getCiyReducer({ state_id: stateCode[0]?.id }));
            }
        }
    };

    const handleDateChange: HandleDateChangeType = (date, name) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        setFormStates({ ...formStates, [name]: formattedDate })
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        setSearchValue("");
    };

    const fetchManageEmployee = async (data: { companyId: number, status: number, search?: string }) => {
        try {
            setLoading(true)
            const result = await getManageEmployeeApi(data);
            if (result?.success) {
                setLoading(false)
                setEmployeeData(result?.employeeStatus)
            }
        } catch (error: any) {
            setLoading(false)
            toast.error(error?.data?.errors || error?.message);
        }
    }

    useEffect(() => {
        if (LoginData) {
            if (searchValue) {
                const cleanFunction = setTimeout(() => {
                    fetchManageEmployee({ companyId: LoginData.companyId, status: value, search: searchValue });
                }, 1000)
                return () => clearInterval(cleanFunction);
            } else {
                fetchManageEmployee({ companyId: LoginData.companyId, status: value, search: '' });
            }
        }
    }, [value, searchValue]);

    useEffect(() => {
        dispatch(getStateReducer());
    }, [])

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const sendData = {
                "name": formStates.name,
                "email": formStates.email,
                "dateofbirth": formStates.dateOfBirth,
                "gender": formStates.gender,
                "city": formStates.city,
                "state": formStates.state,
                "zip_code": formStates.zipCode,
                "phone": formStates?.phone,
                "companyId": LoginData?.companyId
            }
            setBtnLoading(true)
            const result = await addManageEmployeeApi(sendData);
            if (result.success) {
                setBtnLoading(false)
                toast.success(result?.message);
                setopenAddModal(false);
                LoginData && fetchManageEmployee({ companyId: LoginData.companyId, status: value, search: '' });
                setFormStates({
                    name: '',
                    email: '',
                    phone: '',
                    dateOfBirth: '',
                    gender: '',
                    currentLocation: '',
                    city: '',
                    state: '',
                    zipCode: '',
                })
            }
        } catch (error: any) {
            setBtnLoading(false)
            toast.error(error?.data?.errors || error?.message);
        }
    }
    return (
        <CustomContainer>
            <div className={'AllPageMain'}>
                <div className={'HeadAndButton'}>
                    <h2>Manage Employees</h2>
                </div>
                <div className='grid grid-cols-1 lg:grid-cols-3 lg:gap-6 gap-y-6 mt-8'>
                    <div className={styles.cards}>
                        <div className={styles.cardContent}>
                            <h3>Newly Added</h3>
                            <p>{employeeData?.employeeNewlyAdded}</p>
                        </div>
                    </div>
                    <div className={styles.cards}>
                        <div className={styles.cardContent}>
                            <h3>Disabled</h3>
                            <p>{employeeData?.employeeDisabled}</p>
                        </div>
                    </div>
                    <div className={styles.cards}>
                        <div className={styles.cardContent}>
                            <h3>Already in the system</h3>
                            <p>{employeeData?.employeeInSystem}</p>
                        </div>
                    </div>
                </div>
                <div className={`${styles.appointmentsMain} mt-8`}>
                    {
                        manageEmployee?.canEdit &&
                        <button className='fillButton ml-auto' onClick={() => setopenAddModal(true)}>Add employee +</button>
                    }
                    <Tabs value={value} onChange={handleChange} className="custumTabs">
                        <Tab label="All" {...a11yProps(0)} />
                        <Tab label="Newly Added" {...a11yProps(1)} />
                        <Tab label="Disabled" {...a11yProps(2)} />
                    </Tabs>
                    <CustomTabPanel value={value} index={0}>
                        <AllTable
                            employeeData={employeeData?.empDetails}
                            loading={loading}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                            handleOpenConfirmModal={handleOpenConfirmModal}
                        />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={1}>
                        <AddedTable
                            employeeData={employeeData?.empDetails}
                            loading={loading}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                            handleOpenConfirmModal={handleOpenConfirmModal}
                        />
                    </CustomTabPanel>
                    <CustomTabPanel value={value} index={2}>
                        <DisableTable
                            employeeData={employeeData?.empDetails}
                            loading={loading}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                            handleOpenConfirmModal={handleOpenConfirmModal}
                        />
                    </CustomTabPanel>
                </div>
            </div>
            <ConfirmModal
                openConfirmModal={openConfirmModal}
                CloseConfirmModal={CloseConfirmModal}
                confirmSubmit={confirmSubmit}
                btnLoading={btnLoading}
                title={`Are you sure you want to ${particularData?.status === "Active" ? "disable" : "enable"}  this employee?`}
            />
            <AddEmployee
                openAddModal={openAddModal}
                closeAddModal={closeAddModal}
                handleSubmit={handleSubmit}
                btnLoading={btnLoading}
                title={`Add Employee`}
                formStates={formStates}
                handleInputChange={handleInputChange}
                handleDateChange={handleDateChange}
                handleSelectChange={handleSelectChange}
                stateData={stateData}
                cityData={cityData}
            />
        </CustomContainer>
    )
}

export default ManageEmploye